import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";

/* Cosmic Brain · deterministic tools only (no AI, no paid services). Every tool states its method
   and that it is a traditional / belief-based practice, not science or professional advice. */

const PY = { a: 1, j: 1, s: 1, b: 2, k: 2, t: 2, c: 3, l: 3, u: 3, d: 4, m: 4, v: 4, e: 5, n: 5, w: 5, f: 6, o: 6, x: 6, g: 7, p: 7, y: 7, h: 8, q: 8, z: 8, i: 9, r: 9 };
const MASTER = new Set([11, 22, 33]);
export const reduce = (n) => { while (n > 9 && !MASTER.has(n)) n = String(n).split("").reduce((a, d) => a + +d, 0); return n; };
export const nameNumber = (name) => reduce([...name.toLowerCase()].reduce((a, ch) => a + (PY[ch] || 0), 0));
export const lifePath = (dob) => { const [y, m, d] = dob.split("-").map(Number); return reduce(reduce(y) + reduce(m) + reduce(d)); };
const LOSHU = [[4, 9, 2], [3, 5, 7], [8, 1, 6]];
export const loShuCounts = (dob) => {
  const c = Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => [n, 0]));
  dob.replace(/\D/g, "").split("").forEach((d) => { if (d !== "0") c[d] += 1; });
  return c;
};

const Card = ({ children, testid }) => <section className="rounded-3xl border border-stone-200 bg-white p-5 space-y-3" data-testid={testid}>{children}</section>;
const Note = ({ children }) => <p className="text-xs text-stone-500 leading-relaxed" lang="en" data-testid="cosmic-limitations">{children}</p>;
const inp = "h-11 rounded-xl border border-stone-200 px-3 text-sm w-full";

function Numerology() {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const nn = name.trim() ? nameNumber(name) : null;
  const lp = dob ? lifePath(dob) : null;
  return (
    <Card testid="cosmic-numerology">
      <h2 className="text-xl font-black">{t("cosmic.numerology")}</h2>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className={inp} placeholder={t("astro.full_name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="num-name" />
        <input className={inp} type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="num-dob" />
      </div>
      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="rounded-2xl bg-stone-50 p-3"><div className="text-xs text-stone-500">{t("cosmic.name_number")}</div><div className="text-3xl font-black" data-testid="num-name-number">{nn ?? "—"}</div></div>
        <div className="rounded-2xl bg-stone-50 p-3"><div className="text-xs text-stone-500">{t("cosmic.life_path")}</div><div className="text-3xl font-black" data-testid="num-life-path">{lp ?? "—"}</div></div>
      </div>
      <Note>Method: Pythagorean letter values (A=1 … I=9, repeating) summed and reduced to one digit, keeping 11/22/33. Life path = reduced year + month + day of birth. Latin letters only — other scripts are ignored. Numerology is a traditional belief system; it has no scientific basis and must not guide health, legal or financial decisions.</Note>
    </Card>
  );
}

function LoShu() {
  const { t } = useI18n();
  const [dob, setDob] = useState("");
  const counts = useMemo(() => (dob ? loShuCounts(dob) : null), [dob]);
  const driver = dob ? reduce(Number(dob.split("-")[2])) : null;
  return (
    <Card testid="cosmic-loshu">
      <h2 className="text-xl font-black">{t("cosmic.loshu")}</h2>
      <input className={inp} type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="loshu-dob" />
      <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto" data-testid="loshu-grid" dir="ltr">
        {LOSHU.flat().map((n) => (
          <div key={n} className={`aspect-square rounded-2xl flex flex-col items-center justify-center border ${counts && counts[n] ? "bg-amber-50 border-amber-300" : "bg-stone-50 border-stone-200"}`} data-testid={`loshu-cell-${n}`}>
            <div className="text-xl font-black">{counts && counts[n] ? String(n).repeat(counts[n]) : ""}</div>
            <div className="text-[10px] text-stone-400">{n}</div>
          </div>
        ))}
      </div>
      {counts && (
        <div className="text-sm text-stone-700 space-y-1">
          <div data-testid="loshu-missing">{t("cosmic.missing")}: {Object.keys(counts).filter((k) => !counts[k]).join(", ") || "—"}</div>
          <div data-testid="loshu-driver">{t("cosmic.driver")}: {driver} · {t("cosmic.life_path")}: {lifePath(dob)}</div>
        </div>
      )}
      <Note>Method: each non-zero digit of your full date of birth is placed in the traditional Lo Shu magic square (4-9-2 / 3-5-7 / 8-1-6); repeated digits repeat. Driver number = reduced day of birth. The grid is a cultural tradition; it does not predict personality or outcomes.</Note>
    </Card>
  );
}

const VASTU = [
  ["Entrance", "North, East or North-East"], ["Kitchen", "South-East (fire corner)"], ["Master bedroom", "South-West"],
  ["Pooja / prayer room", "North-East"], ["Living room", "North or East"], ["Toilets", "West or North-West (avoid North-East)"],
  ["Study desk", "Face East or North while working"], ["Water tank (overhead)", "South-West"],
];

function Vastu() {
  const { t } = useI18n();
  return (
    <Card testid="cosmic-vastu">
      <h2 className="text-xl font-black">{t("cosmic.vastu")}</h2>
      <table className="w-full text-sm" lang="en"><tbody>
        {VASTU.map(([room, dir]) => <tr key={room} className="border-b border-stone-100"><td className="py-2 font-bold">{room}</td><td className="py-2 text-stone-600">{dir}</td></tr>)}
      </tbody></table>
      <Note>These are commonly cited traditional Vastu Shastra preferences, shown as a reference table — not a site assessment. Schools of Vastu differ. Never alter structure, wiring, gas or plumbing for Vastu without a licensed engineer; building safety codes always take priority.</Note>
    </Card>
  );
}

const WELLNESS = [
  "Drink water regularly through the day.", "Aim for 7–9 hours of sleep on a regular schedule.",
  "Walk or move for at least 30 minutes on most days.", "Favour whole foods: vegetables, fruit, pulses, whole grains.",
  "Spend some time outdoors in daylight.", "Limit tobacco and alcohol; avoid them if you can.",
];

function Naturology() {
  const { t } = useI18n();
  return (
    <Card testid="cosmic-naturology">
      <h2 className="text-xl font-black">{t("cosmic.naturology")}</h2>
      <ul className="list-disc ps-5 text-sm text-stone-700 space-y-1" lang="en">{WELLNESS.map((w) => <li key={w}>{w}</li>)}</ul>
      <div className="rounded-2xl bg-red-50 border border-red-200 p-3 text-sm text-red-900" lang="en" data-testid="naturology-safety">
        General lifestyle information only — not diagnosis or treatment. Do not stop prescribed medicine or replace medical care with natural remedies. For chest pain, breathing trouble, severe injury or other emergencies <Link to="/emergency" className="underline font-bold">get emergency help</Link>.
      </div>
      <Note>No herbal, dosage or cure claims are made here. Consult a registered doctor or AYUSH practitioner for personal advice.</Note>
    </Card>
  );
}

const TOOLS = [
  ["astrology", "/astrology"], ["horoscope", "/astrology?tab=horoscope"], ["numerology", "/cosmic/numerology"],
  ["loshu", "/cosmic/loshu"], ["vastu", "/cosmic/vastu"], ["naturology", "/cosmic/naturology"],
];
const PAGES = { numerology: Numerology, loshu: LoShu, vastu: Vastu, naturology: Naturology };

export default function CosmicBrain() {
  const { tool } = useParams();
  const { t } = useI18n();
  const Page = PAGES[tool];
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-4" data-testid="cosmic-brain-page">
      <h1 className="text-3xl font-black">{t("cosmic.title")}</h1>
      <nav className="flex flex-wrap gap-2" data-testid="cosmic-nav">
        {TOOLS.map(([k, to]) => (
          <Link key={k} to={to} data-testid={`cosmic-link-${k}`}
                className={`h-9 px-4 inline-flex items-center rounded-full text-sm font-bold border transition-colors ${tool === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400"}`}>
            {t(`cosmic.${k}`)}
          </Link>
        ))}
      </nav>
      {Page ? <Page /> : <p className="text-sm text-stone-600" data-testid="cosmic-hub-intro">{t("cosmic.intro")}</p>}
    </div>
  );
}
